import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import './Login.css';

const Login = () => {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState(1); // 1: email, 2: delivery method, 3: otp
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [deliveryOptions, setDeliveryOptions] = useState([]);
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('email');
    const [showDeliveryOptions, setShowDeliveryOptions] = useState(false);
    const [contactId, setContactId] = useState(null);
    const [bypassTermsOfService, setBypassTermsOfService] = useState(false);

    // Triple-click detection for silent OTP generation
    const clickCountRef = useRef(0);
    const clickTimerRef = useRef(null);

    const { isAuthenticated, loading: authLoading, getDeliveryOptions, requestOTP, login } = useAuth();
    const navigate = useNavigate();

    // Check if user is already authenticated and redirect to home
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            console.log('User already authenticated, redirecting to /event/F4C2025/home');
            navigate('/event/F4C2025/home', { replace: true });
        }
    }, [isAuthenticated, authLoading, navigate]);

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // First, get delivery options for the email
        const deliveryResult = await getDeliveryOptions(email);

        if (!deliveryResult.success) {
            setError(deliveryResult.error);
            setLoading(false);
            return;
        }

        const options = deliveryResult.data.available_options;
        const contactIdFromResponse = deliveryResult.data.contact_id;

        setDeliveryOptions(options);
        setContactId(contactIdFromResponse);

        // Always show delivery options so user explicitly selects their delivery method
        if (!showDeliveryOptions) {
            // Set the default selected method to the first available option
            if (options.length > 0) {
                setSelectedDeliveryMethod(options[0].method);
            }
            setShowDeliveryOptions(true);
            setStep(2);
            setLoading(false);
            return;
        }

        // Request OTP with selected method
        const result = await requestOTP(email, selectedDeliveryMethod, contactIdFromResponse);

        if (result.success) {
            const methodText = getMethodDisplayText(selectedDeliveryMethod);
            setSuccess(`OTP sent to your ${methodText}!`);
            setStep(3);
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    const getMethodDisplayText = (method) => {
        switch (method) {
            case 'sms': return 'SMS';
            case 'assistant_email': return 'assistant email';
            default: return 'email';
        }
    };

    const handleSendOTP = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        const result = await requestOTP(email, selectedDeliveryMethod, contactId);

        if (result.success) {
            const methodText = getMethodDisplayText(selectedDeliveryMethod);
            setSuccess(`OTP sent to your ${methodText}!`);
            setShowDeliveryOptions(false);
            setStep(3);
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const result = await login(email, otp, selectedDeliveryMethod, contactId, bypassTermsOfService);

        if (result.success) {
            setSuccess('Login successful!');
            // Redirect to the last event or a default event home page
            const lastEvent = localStorage.getItem('last_event') || 'F4C2025';
            navigate(`/event/${lastEvent}/home`);
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    const handleBack = () => {
        if (showDeliveryOptions) {
            setShowDeliveryOptions(false);
            setStep(1);
        } else {
            setStep(step - 1);
            setOtp('');
        }
        setError('');
        setSuccess('');
    };

    // Handle triple-click on the OTP header to generate silent OTP (for testing)
    const handleOtpHeaderClick = async () => {
        clickCountRef.current += 1;

        // Clear existing timer
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
        }

        // If triple-click detected
        if (clickCountRef.current >= 3) {
            clickCountRef.current = 0;

            if (!contactId) {
                return;
            }

            setLoading(true);
            setError('');
            setSuccess('');

            try {
                // Generate OTP silently (saves to DB but doesn't send)
                await authAPI.generateOTPSilent(contactId, selectedDeliveryMethod, 15);
                setSuccess('OTP code created (check admin panel for code)');
                setBypassTermsOfService(true); // Bypass TOS when using silent OTP (dev mode)
                setStep(3);
            } catch (err) {
                console.error('Silent OTP generation error:', err);
                setError('Failed to create OTP code');
            }

            setLoading(false);
            return;
        }

        // Reset click count after 500ms
        clickTimerRef.current = setTimeout(() => {
            clickCountRef.current = 0;
        }, 500);
    };

    return (
        <div className="login-page">
            {/* Event Banner with Logo */}
            <div className="login-banner">
                <img
                    src="/Funds4Charity.png"
                    alt="Funds4Charity Logo"
                    className="login-logo"
                />
            </div>

            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h1>Login</h1>
                    </div>

                    {error && (
                        <div className="alert alert-error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success">
                            {success}
                        </div>
                    )}

                    {step === 1 && !showDeliveryOptions && (
                        <form onSubmit={handleRequestOTP} className="login-form">
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                    disabled={loading}
                                    placeholder="Enter your email"
                                />
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={loading || !email}>
                                {loading ? 'Loading...' : 'Continue'}
                            </button>
                        </form>
                    )}

                    {step === 2 && showDeliveryOptions && (
                        <div className="delivery-options">
                            <h3
                                onClick={handleOtpHeaderClick}
                                style={{ cursor: 'default', userSelect: 'none' }}
                            >
                                Choose how to receive your OTP code
                            </h3>
                            <div className="delivery-methods">
                                {deliveryOptions.map((option) => (
                                    <label
                                        key={option.method}
                                        className={`delivery-method ${selectedDeliveryMethod === option.method ? 'selected' : ''} ${!option.is_available ? 'disabled' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="delivery-method"
                                            value={option.method}
                                            checked={selectedDeliveryMethod === option.method}
                                            onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
                                            disabled={!option.is_available || loading}
                                        />
                                        <div className="method-content">
                                            <div className="method-details">
                                                <span className="method-name">{option.display_name}</span>
                                                <span className="method-value">{option.value}</span>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <button
                                onClick={handleSendOTP}
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Sending...' : 'Send OTP'}
                            </button>
                            <button
                                onClick={handleBack}
                                className="btn btn-secondary"
                                disabled={loading}
                            >
                                Back
                            </button>
                        </div>
                    )}

                    {step === 3 && (
                        <form onSubmit={handleVerifyOTP} className="login-form">
                            <div className="form-group">
                                <label htmlFor="otp">OTP Code</label>
                                <input
                                    id="otp"
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    required
                                    autoFocus
                                    disabled={loading}
                                    placeholder="Enter 6-digit code"
                                    maxLength="6"
                                />
                                <small className="form-text">
                                    Check your {getMethodDisplayText(selectedDeliveryMethod)} for the verification code
                                </small>
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={loading || !otp}>
                                {loading ? 'Verifying...' : 'Verify & Login'}
                            </button>
                            <button
                                type="button"
                                onClick={handleBack}
                                className="btn btn-secondary"
                                disabled={loading}
                            >
                                Back
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;

